import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, CheckCircle, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import SEOLayout from '@/components/SEOLayout';

const ForLaidOffTech = () => {
  const navigate = useNavigate();

  return (
    <SEOLayout title="After a Tech Layoff: Build an AI Automation Career | HumanScaled" description="Transition from tech employment to AI infrastructure operator. Monetize your automation skills across multiple clients. No job applications.">
      <section className="py-20 md:py-28">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24 space-y-6">
          <p className="text-sm text-emerald-400 font-mono uppercase tracking-wider">For engineers, PMs, and technical professionals</p>
          <h1 className="text-4xl md:text-5xl font-black tracking-tighter">You Were Laid Off.<br /><span className="text-emerald-400">Your Skills Were Not.</span></h1>
          <p className="text-lg text-muted-foreground max-w-2xl">The companies that let you go still need the work done. Now you can do it for multiple companies at once — on your own terms, with AI infrastructure behind you.</p>
          <Button onClick={() => navigate('/signup')} className="bg-emerald-500 text-black hover:bg-emerald-400 h-12 px-8 font-bold rounded-sm">Start Building <ArrowRight className="ml-2 w-5 h-5" /></Button>
        </div>
      </section>

      <section className="py-16 bg-[#0a0a0a]/50 border-t border-white/5">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24">
          <h2 className="text-2xl font-bold mb-8">The Transition Path</h2>
          <div className="space-y-6">
            {[
              { step: '01', title: 'Identify what you already know', desc: 'You have automation skills, process thinking, and technical intuition. These are exactly what businesses need but cannot find or afford to hire.' },
              { step: '02', title: 'Package it as a service', desc: 'Use HumanScaled to build workflows that solve specific problems: scheduling, lead qualification, data processing, customer support. Describe outcomes — the platform builds the automation.' },
              { step: '03', title: 'Serve multiple clients', desc: 'The same workflow serves multiple businesses. Each client pays a monthly fee. Your income scales with clients, not hours worked.' },
              { step: '04', title: 'Build portfolio income', desc: 'Three clients at $900/mo is $2,700 minus $497 platform = $2,203/mo margin. Five clients: $4,003/mo. Ten: $8,503/mo. No interviews. No permission.' },
            ].map((item, i) => (
              <div key={i} className="flex gap-6 items-start">
                <span className="font-mono text-2xl font-bold text-emerald-400/30 flex-shrink-0 w-12">{item.step}</span>
                <div><h3 className="font-bold mb-1">{item.title}</h3><p className="text-sm text-muted-foreground">{item.desc}</p></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 border-t border-white/5">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24">
          <h2 className="text-2xl font-bold mb-8">Why Infrastructure Beats Another Job Application</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="border border-red-500/20 bg-red-500/5 p-6">
              <p className="text-xs text-red-400 uppercase tracking-wider mb-3">Traditional Path</p>
              <ul className="space-y-2 text-sm text-muted-foreground">
                {['Send 200+ applications', 'Wait months for responses', 'Compete against thousands', 'Single employer dependency', 'Salary ceiling determined by others', 'Risk of another layoff'].map((item, i) => (
                  <li key={i} className="flex items-start gap-2"><span className="text-red-400 mt-1">-</span>{item}</li>
                ))}
              </ul>
            </div>
            <div className="border border-emerald-500/20 bg-emerald-500/5 p-6">
              <p className="text-xs text-emerald-400 uppercase tracking-wider mb-3">Operator Path</p>
              <ul className="space-y-2 text-sm text-muted-foreground">
                {['Build once, serve many', 'Revenue from week one', 'No gatekeepers', 'Diversified income sources', 'You set the pricing', 'No one can lay you off'].map((item, i) => (
                  <li key={i} className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />{item}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#0a0a0a]/50 border-t border-white/5">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24">
          <h2 className="text-2xl font-bold mb-6">Skills You Already Have That Transfer</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {['Process automation', 'API integrations', 'Data pipeline design', 'Project management', 'Technical documentation', 'Problem decomposition', 'Client communication', 'Quality assurance', 'System architecture'].map((skill, i) => (
              <div key={i} className="flex items-center gap-2 p-3 bg-[#0a0a0a] border border-white/5 text-sm"><CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />{skill}</div>
            ))}
          </div>
          <p className="text-sm text-muted-foreground mt-6">You do not need to learn new skills. You need a new delivery model for the skills you already have.</p>
        </div>
      </section>

      <section className="py-16 border-t border-white/5 text-center">
        <div className="max-w-2xl mx-auto px-6">
          <h2 className="text-2xl font-bold mb-3">You were never the liability.<br /><span className="text-emerald-400">The single-employer model was.</span></h2>
          <p className="text-muted-foreground mb-6">Build something no one can take from you.</p>
          <div className="flex justify-center gap-4">
            <Button onClick={() => navigate('/signup')} className="bg-emerald-500 text-black hover:bg-emerald-400 h-12 px-8 font-bold rounded-sm">Start Building <ArrowRight className="ml-2 w-5 h-5" /></Button>
            <Button variant="outline" onClick={() => navigate('/for-operators')} className="border-white/10 h-12 px-6 rounded-sm">Learn More <ChevronRight className="ml-1 w-4 h-4" /></Button>
          </div>
        </div>
      </section>
    </SEOLayout>
  );
};

export default ForLaidOffTech;
